import {
  Alert,
  Box,
  Button,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseButton from 'components/@home/drawers/CloseButton';
import Loading from 'components/common/Loading';
import {
  buildInitialFormValues,
  buildTranslationsBody,
  CONTENT_LOCALE_CODES,
  FORM_HTML_FIELD_BY_LOCALE,
} from 'components/@admin/@content/contentLocaleUtils';
import Editor from 'components/formik/Editor';
import TextField from 'components/formik/TextField';
import { Field, Form, Formik } from 'formik';
import { bool, func, object } from 'prop-types';
import React, { Suspense, useEffect, useState } from 'react';
import { useCreateContentMutation, useEditContentMutation } from 'store/app/entities/ContentsSlice';
import { I18n } from 'utils/i18n';

const ContentDrawer = ({ content = null, isOpen, onClose }) => {
  const [createContent, createStatus] = useCreateContentMutation();
  const [editContent, editStatus] = useEditContentMutation();
  const isLoading = createStatus.isLoading || editStatus.isLoading;
  const isError = createStatus.isError || editStatus.isError;
  const error = createStatus.error || editStatus.error;

  const isCreate = content != null && !content._id;
  const [activeLocale, setActiveLocale] = useState('en');

  useEffect(() => {
    setActiveLocale('en');
  }, [content?._id, isCreate]);

  const handleSubmit = async values => {
    const body = {
      name: values.name,
      value: values.htmlEn || '',
      translations: buildTranslationsBody(values, isCreate ? null : content),
    };
    const ret = isCreate
      ? await createContent(body)
      : await editContent({ _id: content._id, body });
    if (!ret.error) {
      onClose();
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      {content != null ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100%', md: 900 },
            maxWidth: '100vw',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <CloseButton onClick={onClose} />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, pb: 2.5 }}>
            <Typography variant="h6" component="h1" sx={{ mb: 2 }}>
              {isCreate
                ? I18n.t('ContentDrawer.TitleCreate')
                : I18n.t('ContentDrawer.Title', { name: content.name })}
            </Typography>
            {isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error?.data?.message || error?.error || I18n.t('ContentDrawer.SaveError')}
              </Alert>
            )}
            <Formik
              key={isCreate ? 'new' : content._id}
              enableReinitialize
              initialValues={buildInitialFormValues(content)}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Field
                    name="name"
                    component={TextField}
                    fullWidth
                    label={I18n.t('ContentDrawer.Name')}
                    margin="normal"
                    disabled={isLoading}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    {I18n.t('ContentDrawer.Language')}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={activeLocale}
                    onChange={(e, v) => {
                      if (v != null) setActiveLocale(v);
                    }}
                    size="small"
                    color="primary"
                    sx={{
                      flexWrap: 'wrap',
                      mb: 2,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                      },
                    }}
                  >
                    {CONTENT_LOCALE_CODES.map(code => (
                      <ToggleButton key={code} value={code}>
                        {I18n.t(`ContentDrawer.Locale.${code}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    {I18n.t('ContentDrawer.BodyHint')}
                  </Typography>
                  <Box sx={{ minHeight: 440 }}>
                    <Suspense fallback={<Loading size={40} />}>
                      <Field
                        key={activeLocale}
                        name={FORM_HTML_FIELD_BY_LOCALE[activeLocale]}
                        component={Editor}
                      />
                    </Suspense>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading || isSubmitting}
                    >
                      {I18n.t('ContentDrawer.Save')}
                    </Button>
                    <Button type="button" onClick={onClose} disabled={isLoading}>
                      {I18n.t('ContentDrawer.Cancel')}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      ) : null}
    </Drawer>
  );
};

ContentDrawer.propTypes = {
  content: object,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
};

export default ContentDrawer;
